import { useEffect, useRef, useState } from "react";
import Persona from "persona";

import { Loader, Stack } from "@braintrust/braintrust-ui-components";
import { Modal } from "@js/components/modal";
import { Snackbar } from "@js/components/snackbar";
import { useAppDispatch } from "@js/hooks";

import { useGetIdentityVerificationInquiryQuery } from "../../api";
import { useUserNeedsIdentityVerification } from "../../hooks/use-user-needs-identity-verification";
import type { UseWaitForIdentityVerificationResultConfig } from "../../hooks/use-wait-for-identity-verification-result";
import { useWaitForIdentityVerificationResult } from "../../hooks/use-wait-for-identity-verification-result";
import { openIdentityVerificationUnsuccessfulModal } from "../identity-verification-unsuccessful-modal";

import styles from "./styles.module.scss";

export const IdentityVerificationModalInstance = Modal("identity-verification");

export const openIdentityVerificationModal = (
  props: IdentityVerificationModalProps = {},
) => {
  IdentityVerificationModalInstance.open({
    closeButton: false,
    children: <IdentityVerificationModal {...props} />,
    className: styles.modal,
    keepOnBackdropClick: true,
    padding: false,
    containerScrollableNoMobilePadding: true,
  });
};

const closeIdentityVerificationModal = () => {
  IdentityVerificationModalInstance.close();
};

type IdentityVerificationModalProps = {
  onVerificationClose?: () => void;
  isAcceptOfferFlow?: boolean;
} & UseWaitForIdentityVerificationResultConfig;

const IdentityVerificationModal = ({
  onVerificationFail,
  onVerificationSuccess,
  onVerificationClose,
  isAcceptOfferFlow,
}: IdentityVerificationModalProps) => {
  const [wasLaunched, setWasLaunched] = useState(false);
  const { waitForIdentityVerificationResult } =
    useWaitForIdentityVerificationResult();
  const dispatch = useAppDispatch();

  const { userNeedsIdentityVerification } = useUserNeedsIdentityVerification();
  const shouldLaunchIdentityVerification = userNeedsIdentityVerification;
  const { data, isLoading } = useGetIdentityVerificationInquiryQuery(
    undefined,
    { skip: !shouldLaunchIdentityVerification },
  );

  const isInitRef = useRef(false);
  useEffect(() => {
    if (!data) {
      return;
    }
    if (isInitRef.current) {
      return;
    }

    setWasLaunched(true);
    isInitRef.current = true;

    try {
      const client = new Persona.Client({
        environmentId: data.environment_id,
        inquiryId: data.inquiry_id,
        sessionToken: data.session_token ?? undefined,
        onReady: () => {
          client.open();
        },
        onComplete: async () => {
          waitForIdentityVerificationResult({
            onVerificationFail: () => {
              closeIdentityVerificationModal();
              openIdentityVerificationUnsuccessfulModal({
                isAcceptOfferFlow,
                onClose: () => {
                  onVerificationFail?.();
                },
              });
            },
            onVerificationSuccess: () => {
              closeIdentityVerificationModal();
              onVerificationSuccess?.();
            },
          });
        },
        onCancel: () => {
          closeIdentityVerificationModal();
          onVerificationClose?.();
        },
        onError: () => {
          Snackbar.error("Something went wrong!");
          onVerificationClose?.();
        },
      });
    } catch (_error) {
      Snackbar.error("Something went wrong!");
    }
  }, [
    isAcceptOfferFlow,
    data,
    dispatch,
    waitForIdentityVerificationResult,
    onVerificationFail,
    onVerificationSuccess,
    onVerificationClose,
  ]);

  const showEmptyState =
    !isLoading && shouldLaunchIdentityVerification && !data && !wasLaunched;
  const showVerificationNotRequired =
    !isLoading && !shouldLaunchIdentityVerification && !wasLaunched;

  useEffect(() => {
    if (!showVerificationNotRequired) {
      return;
    }

    closeIdentityVerificationModal();
    onVerificationClose?.();
    setTimeout(() => {
      Snackbar.info("Identity is not required.");
    }, 1000);
  }, [showVerificationNotRequired, onVerificationClose]);

  useEffect(() => {
    if (!showEmptyState) {
      return;
    }
    closeIdentityVerificationModal();
    onVerificationClose?.();
    setTimeout(() => {
      Snackbar.warning("Identity verification is not available.", {
        autoHideDuration: 10_000,
      });
    }, 1000);
  }, [showEmptyState, onVerificationClose]);

  return (
    <Stack>
      <Loader size={50} />
    </Stack>
  );
};
